import { useSuspenseQuery } from "@tanstack/react-query";

import useGetAccessToken from "components/Login/useGetAccessToken";
import type { ChatThread } from "types/ChatThread";
import { getThreads } from "../methods/getThreads";

interface UseThreadsSuspenseQueryOptions<T> {
  select?: (threads: ChatThread[]) => T;
}

/**
 * Suspense-flavored variant of useThreadsQuery. Throws a promise while the
 * threads list is loading and an error if the request fails. Callers receive
 * a non-nullable `data`. Shares the same query key as useThreadsQuery, so
 * mutations and the non-Suspense hook see one cache.
 *
 * Focus invalidation is intentionally not enabled here; see useThreadsQuery
 * for the manual-refetch convention.
 */
export default function useThreadsSuspenseQuery<T = ChatThread[]>(
  opts?: UseThreadsSuspenseQueryOptions<T>
) {
  const getAccessToken = useGetAccessToken();

  return useSuspenseQuery({
    queryKey: ["cc-db-api", "threads"],
    queryFn: async () => {
      // Defer to a microtask so MSAL's setState (triggered by getAccessToken
      // inside the queryFn) doesn't fire synchronously during the render that
      // suspends.
      // https://github.com/AzureAD/microsoft-authentication-library-for-js/issues/5796#issuecomment-1763461620
      await Promise.resolve();
      return getThreads(getAccessToken);
    },
    retry: false,
    select: opts?.select,
  });
}
